import { useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { APIClient } from '../utils/APIClient';
import { LocalStorage } from '../utils/LocalStorage';
import { API_URL } from '../settings/local_settings';
import { isOlderThanRecentRedemption } from '../components/trial_code/trialCodeApi';

// Status que requieren redireccion
const STATUS_NEVER_ACTIVE = 'Never Active';
const STATUS_CHURNED = 'Churned';

// Paginas donde no se debe hacer el check (ya esta en la pagina de error)
const EXCLUDED_PATHS = ['/free-trial-ended', '/subscription-ended', '/login', '/signup', '/agency/subscription'];

// Debounce en milisegundos (30 segundos)
const DEBOUNCE_MS = 30000;

// Sincronizar credenciales OAuth desconectadas (banner global), de la agencia `agencyId`.
function refreshDisconnectedCredentials(agencyId, isStale) {
    new APIClient(API_URL + 'agency/disconnected-credentials-summary/').call('GET').then((data) => {
        if (isStale()) return;
        if (data && data.status === 'OK' && data.has_disconnected_credentials !== undefined) {
            LocalStorage.updateDisconnectedCredentials(agencyId, data.has_disconnected_credentials, data.channels || [],
                data.count);
        }
    }).catch(() => {
        // Silencioso: si falla, el banner simplemente no se actualiza este ciclo
    });
}

export const useAgencyStatusRefresh = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const lastCheckRef = useRef(0);

    const shouldSkipCheck = useCallback(() => {
        // No hay datos de usuario
        if (!LocalStorage.hasUserData()) {
            return true;
        }

        // No es AGENCY_USER
        if (LocalStorage.fetchUserType() !== 'AGENCY_USER') {
            return true;
        }

        // Esta en una pagina excluida
        if (EXCLUDED_PATHS.includes(location.pathname)) {
            return true;
        }

        // Debounce: paso menos de 30 segundos desde el ultimo check
        const now = Date.now();
        if (now - lastCheckRef.current < DEBOUNCE_MS) {
            return true;
        }

        return false;
    }, [location.pathname]);

    const checkAgencyStatus = useCallback(() => {
        if (shouldSkipCheck()) {
            return;
        }

        lastCheckRef.current = Date.now();

        // La sesión que pidió. Una respuesta que vuelve después de un logout, o de entrar con otra
        // cuenta, es de la sesión anterior: no escribe nada (C-app-01).
        const sessionToken = LocalStorage.fetchUserToken();
        const isStale = () => LocalStorage.fetchUserToken() !== sessionToken;

        const url = API_URL + 'get-logged-user-data/';
        // `cache` (TTL 30s) no afeita frescura acá: este check ya se auto-debouncea 30s con
        // `lastCheckRef`, así que la ventana es la misma. Lo que evita es que el check inicial pelee
        // por su propio request contra el provider del marco y el chat de soporte, que piden lo
        // mismo en el mismo tick del arranque.
        new APIClient(url).call('GET', {}, {cache: true}).then((data) => {
            if (!data || data.status !== 'OK' || isStale()) {
                // Error de red, respuesta invalida o de otra sesión: ignorar silenciosamente
                return;
            }

            // Las conexiones caídas se piden con la agencia de ESTA respuesta, y se guardan con
            // ella: el banner sólo muestra el estado de la agencia de la sesión.
            if (data.agency_id) LocalStorage.updateAgencyId(String(data.agency_id));
            refreshDisconnectedCredentials(data.agency_id, isStale);

            // Recién aplicado un código de prueba (CY-T), una respuesta vieja (la caché del backend es por
            // worker) diría «vencida» o menos días: lo de la prueba no se escribe y no se redirige. Lo demás sí.
            const trialIsStale = isOlderThanRecentRedemption(data);
            const newStatus = data.agency_status;
            const currentStatus = LocalStorage.fetchAgencyStatus();

            // Si el status cambio, actualizar localStorage
            if (!trialIsStale && newStatus && newStatus !== currentStatus) {
                LocalStorage.updateAgencyStatus(newStatus);
            }

            // Sincronizar is_dashbo_mcp
            if (data.is_dashbo_mcp !== undefined) {
                LocalStorage.updateIsDashboMcp(data.is_dashbo_mcp);
            }

            // Sincronizar free_trial_days_remaining
            if (!trialIsStale && data.free_trial_days_remaining !== undefined) {
                LocalStorage.updateFreeTrialDaysRemaining(data.free_trial_days_remaining);
            }

            // Sincronizar trial_next_step (el próximo paso de la prueba, para el banner). Un backend
            // que todavía no lo manda no trae la clave: se deja lo que haya y el banner sigue como antes.
            if (!trialIsStale && data.trial_next_step !== undefined) {
                LocalStorage.updateTrialNextStep(data.trial_next_step);
            }

            // Sincronizar has_payment_suspension_warning
            if (data.has_payment_suspension_warning !== undefined) {
                LocalStorage.updateHasPaymentSuspensionWarning(data.has_payment_suspension_warning);
            }

            // Redirigir segun el status
            if (trialIsStale) {
                return;
            }
            if (newStatus === STATUS_NEVER_ACTIVE) {
                navigate('/free-trial-ended');
            } else if (newStatus === STATUS_CHURNED) {
                navigate('/subscription-ended');
            }
        }).catch(() => {
            // Error de red, ignorar silenciosamente para no afectar la sesion del usuario
        });
    }, [shouldSkipCheck, navigate]);

    useEffect(() => {
        // Check inicial al montar
        checkAgencyStatus();

        // Handler para cuando la tab vuelve a ser visible
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                checkAgencyStatus();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [checkAgencyStatus]);
};
